<script setup>
import { ref, computed } from 'vue'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Send, AlertCircle } from 'lucide-vue-next'
import { useChatConfig } from '@/composables/useChatConfig'

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: 'Type your message...'
  }
})

const emit = defineEmits(['send'])

const message = ref('')

// Get chat configuration (loaded from front-config)
const { maxChars, validateMessage } = useChatConfig()

// Character count and validation
const charCount = computed(() => message.value.trim().length)
const validation = computed(() => validateMessage(message.value))

const isValid = computed(() => validation.value.isValid)
const isNearLimit = computed(() => charCount.value > maxChars.value * 0.8)
const isOverLimit = computed(() => charCount.value > maxChars.value)

const counterClass = computed(() => {
  if (isOverLimit.value) return 'text-red-600 font-semibold'
  if (isNearLimit.value) return 'text-orange-500 font-medium'
  return 'text-gray-500'
})

const ariaDescribedBy = computed(() => {
  const ids = ['chat-input-counter']
  if (!isValid.value && charCount.value > 0) {
    ids.push('chat-input-error')
  }
  return ids.join(' ')
})

const sendButtonLabel = computed(() => {
  return `Send message${!isValid.value ? ' (message invalid)' : ''}`
})

const handleSend = () => {
  if (message.value.trim() && !props.disabled && isValid.value) {
    emit('send', message.value)
    message.value = ''
  }
}

const handleKeydown = (event) => {
  // Send on Enter (without Shift)
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    handleSend()
  }
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex gap-2 items-end">
      <div class="flex-1 relative">
        <Textarea
          v-model="message"
          :placeholder="placeholder"
          :disabled="disabled"
          @keydown="handleKeydown"
          :class="[
            'min-h-[60px] max-h-[200px] resize-none',
            { 'border-red-500 focus-visible:ring-red-500': isOverLimit }
          ]"
          :aria-describedby="ariaDescribedBy"
          :aria-invalid="!isValid && charCount > 0"
          rows="2"
        />
      </div>
      <Button
        @click="handleSend"
        :disabled="disabled || !message.trim() || !isValid"
        :aria-label="sendButtonLabel"
        class="h-[60px] px-6"
      >
        <Send class="h-4 w-4" />
      </Button>
    </div>

    <!-- Character counter and validation messages -->
    <div class="flex items-center justify-between text-sm">
      <div class="flex items-center gap-2">
        <span
          id="chat-input-counter"
          :class="counterClass"
          aria-live="polite"
          aria-atomic="true"
        >
          {{ charCount }} / {{ maxChars }} characters
        </span>

        <!-- Validation error message -->
        <div
          v-if="!isValid && charCount > 0"
          id="chat-input-error"
          class="flex items-center gap-1 text-red-600"
          role="alert"
          aria-live="assertive"
        >
          <AlertCircle class="h-4 w-4" aria-hidden="true" />
          <span>{{ validation.error }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
